import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useIsMutating } from '@tanstack/react-query';
import { useShareReport, mutationKeys } from '../../hooks/useReports';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatCostRange } from '../../utils/formatters';
import type { CostRange } from '../../services/reportsApi';
import { parseError } from '../../utils/errorHandlers';
import './ShareReportModal.css';

export interface ShareReviewItem {
  itemId: string;
  /** Display category. May be a user-edited override (resolved upstream in
   *  ReportDetails.selectedReviewItems) rather than the original pipeline value. */
  category: string;
  /** Display severity. May be a user-edited override (resolved upstream in
   *  ReportDetails.selectedReviewItems) rather than the original pipeline value. */
  severity: string;
  /** What this item adds to the total: a point range when the user overrode the
   *  cost; null only for an unedited contractor-eval item (still emailed, just
   *  without a price). */
  costRange: CostRange | null;
}

interface ShareReportModalProps {
  reportId: string;
  selectedItemCount: number;
  totalCostRange: CostRange;
  selectedItems: ShareReviewItem[];
  onClose: () => void;
  onSuccess: () => void;
}

const ShareReportModal: React.FC<ShareReportModalProps> = ({
  reportId,
  selectedItemCount,
  totalCostRange,
  selectedItems,
  onClose,
  onSuccess,
}) => {
  const [includeContractDetails, setIncludeContractDetails] = useState(true);
  const [includeComplianceFlags, setIncludeComplianceFlags] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { mutate: share, isPending } = useShareReport();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  // TanStack Query v5 prefix-match goes one way: a registered mutation matches
  // a filter when the *registered* key starts with the *filter* key. That means
  // the filter must be ≤ the registered key. useUpdateReportItemEdit registers
  // under the bare ['mutations', 'updateReportItem'] (no reportId at hook-mount
  // time), so we filter with the same bare key. The bulk-update hook registers
  // under ['mutations', 'updateReportItem', 'bulk'] — a longer prefix — so the
  // same filter also gates on in-flight bulk updates.
  const isSavingItems =
    useIsMutating({ mutationKey: mutationKeys.updateReportItemBase }) > 0;

  const handleAdvanceToConfirm = () => {
    setConfirming(true);
  };

  const handleBackToEdit = () => {
    setConfirming(false);
  };

  const handleConfirmSend = () => {
    share(
      {
        reportId,
        payload: { includeContractDetails, includeComplianceFlags },
      },
      {
        onSuccess: (data) => {
          showSuccess(`Report sent (${data.itemsShared} items)`);
          onSuccess();
        },
        onError: (error) => {
          showError(`Failed to send report: ${parseError(error).userMessage}`);
        },
      }
    );
  };

  const handleHide = () => {
    if (confirming) {
      setConfirming(false);
      return;
    }
    onClose();
  };

  const sendDisabled = isSavingItems || isPending || selectedItemCount === 0;
  let sendLabel: string;
  if (isSavingItems) sendLabel = 'Saving changes…';
  else if (isPending) sendLabel = 'Sending…';
  else sendLabel = 'Send Report';

  // Mirrors ReportDetails.getSeverityClass so the review list reuses the same
  // .high/.medium/.low/.informational severity styling vocabulary. Unknown
  // severities fall back to 'informational' so styling never breaks.
  const severityClass = (severity: string): string => {
    const s = severity.toLowerCase();
    if (s === 'high' || s === 'critical' || s === 'severe') return 'high';
    if (s === 'medium' || s === 'moderate') return 'medium';
    if (s === 'low' || s === 'minor') return 'low';
    return 'informational';
  };

  return (
    <Modal show onHide={handleHide} centered className="share-report-modal">
      <Modal.Header closeButton>
        <Modal.Title>{confirming ? 'Confirm send' : 'Send Report'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="share-summary">
          <p>
            <strong>{selectedItemCount}</strong> items will be included
          </p>
          <p>
            Total estimated cost: <strong>{formatCostRange(totalCostRange)}</strong>
          </p>
          <p className="share-disclaimer">
            Estimates are a reference baseline derived from licensed national cost data,
            not a contractor quote — local quotes will vary.
          </p>
        </div>
        {!confirming ? (
          <>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="includeContractDetails"
                checked={includeContractDetails}
                onChange={(e) => setIncludeContractDetails(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="includeContractDetails">
                Include Contract &amp; Threshold Notes
              </label>
            </div>
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="includeComplianceFlags"
                checked={includeComplianceFlags}
                onChange={(e) => setIncludeComplianceFlags(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="includeComplianceFlags">
                Include Compliance Flags
              </label>
            </div>
            <p className="text-muted mb-0">
              The inspection report email will be sent to{' '}
              {user?.email ? <strong>{user.email}</strong> : 'your account email'}.
            </p>
          </>
        ) : (
          <div className="share-review">
            <p className="share-review-heading mb-2">
              <strong>Review before sending.</strong> The following{' '}
              {selectedItemCount === 1 ? 'finding' : 'findings'} will be included:
            </p>
            <ul className="share-review-list">
              {selectedItems.map((item) => (
                <li key={item.itemId} className="share-review-item">
                  <span className={`share-review-severity ${severityClass(item.severity)}`}>
                    {item.severity}
                  </span>
                  <span className="share-review-category">{item.category}</span>
                  <span className="share-review-cost">
                    {item.costRange !== null
                      ? formatCostRange(item.costRange)
                      : 'No estimate'}
                  </span>
                </li>
              ))}
            </ul>
            <div className="share-review-content">
              <p className="mb-1">Also included in the email:</p>
              <ul className="mb-2">
                <li>
                  Contract &amp; Threshold Notes:{' '}
                  <strong>{includeContractDetails ? 'Included' : 'Not included'}</strong>
                </li>
                <li>
                  Compliance Flags:{' '}
                  <strong>{includeComplianceFlags ? 'Included' : 'Not included'}</strong>
                </li>
              </ul>
              <p className="text-muted mb-0">
                Sending to{' '}
                {user?.email ? <strong>{user.email}</strong> : 'your account email'}.
              </p>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!confirming ? (
          <>
            <button
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleAdvanceToConfirm}
              disabled={sendDisabled}
            >
              {(isSavingItems || isPending) && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
              {!isSavingItems && !isPending && (
                <i className="bi bi-send me-2"></i>
              )}
              {sendLabel}
            </button>
          </>
        ) : (
          <>
            <button
              className="btn btn-secondary"
              onClick={handleBackToEdit}
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleConfirmSend}
              disabled={sendDisabled}
            >
              {isPending && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
              {!isPending && <i className="bi bi-send me-2"></i>}
              {isPending ? 'Sending…' : 'Confirm Send'}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ShareReportModal;
